import React from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  Globe,
  ScanSearch,
  KeyRound,
  Server,
  Languages,
  Plug,
  FileCheck2,
  Mail,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Public Trust page — single-page summary of AgentFlow's security posture,
 * data handling, supported regulations & languages, integrations. Linked
 * from the landing-page footer ("Trust") and pitch decks. Designed to
 * answer the questions enterprise buyers and SOC2-aware procurement teams
 * ask before they sign up.
 */
export default function Trust() {
  return (
    <div className="min-h-screen bg-white" data-testid="trust-page">
      {/* Brand header */}
      <header className="sticky top-0 z-30 bg-white/80 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/agentflow-logo-512.png" alt="AgentFlow"
                 width="32" height="32" className="h-8 w-8" />
            <span className="font-display font-semibold text-slate-900">
              AgentFlow Compliance
            </span>
          </Link>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-700">
            <Link to="/pricing" className="hover:text-blue-600">Pricing</Link>
            <Link to="/" className="hover:text-blue-600">Home</Link>
            <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white h-9">
              <Link to="/register">Get started</Link>
            </Button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-blue-50/40 via-white to-cyan-50/30 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 py-20">
          <div className="inline-flex items-center gap-2 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-3 py-1 text-xs font-semibold uppercase tracking-wider">
            <ShieldCheck className="h-3.5 w-3.5" />
            Trust at AgentFlow
          </div>
          <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-slate-900 leading-tight">
            Your contracts are{" "}
            <span className="text-blue-600">never used to train AI</span>.
          </h1>
          <p className="mt-6 text-lg text-slate-600 leading-relaxed max-w-2xl">
            We built AgentFlow assuming our customers read their privacy
            policies — and act on what they find. Here's the full picture
            of how we handle your data, what regulations we cover, and the
            integrations you can plug in safely.
          </p>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-5xl mx-auto px-6 pb-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Pillar
            icon={Lock}
            tone="blue"
            title="Encrypted end-to-end"
            body="Documents are encrypted in transit (TLS 1.2+) and at rest. OAuth refresh tokens for Google Drive and Slack are Fernet-encrypted with per-user keys — even a database breach can't replay them."
          />
          <Pillar
            icon={ScanSearch}
            tone="emerald"
            title="Never used for training"
            body="Your data is sandboxed to your workspace. No third-party model provider sees a single byte without your explicit instruction. Sub-processors handle inference only, not retention."
          />
          <Pillar
            icon={KeyRound}
            tone="amber"
            title="Abuse-resistant"
            body="Public APIs are IP- and email-rate-limited (5/hr, 20/day, 10/email/day). Workspace-level cost alerts flag anomalous usage. Per-user Fernet keys ensure token compromise stays contained."
          />
        </div>
      </section>

      {/* Regulations + Languages */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card>
            <H3 icon={Globe}>Regulations we cite</H3>
            <ul className="mt-4 space-y-2 text-sm text-slate-700">
              {[
                ["GDPR + CCPA + HIPAA", "Global baseline for data privacy"],
                ["SOC 2 + PCI-DSS", "Vendor security & cardholder data"],
                ["FTC consumer protection", "US fair-dealing rules"],
                ["LOPDGDD + RGPD (Spain)", "Spanish data protection"],
                ["LGPD (Brazil/Portugal)", "Brazilian/Portuguese privacy"],
                ["CNIL + RGPD (France)", "French data authority guidance"],
                ["DSGVO (Germany)", "German implementation of GDPR"],
              ].map(([name, desc]) => (
                <li key={name} className="flex items-start gap-2">
                  <FileCheck2 className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
                  <span>
                    <strong className="font-semibold text-slate-900">{name}</strong>
                    <span className="text-slate-500"> — {desc}</span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-xs text-slate-500 leading-relaxed">
              Custom frameworks (industry-specific or regional) available
              on Business plan by request.
            </p>
          </Card>

          <Card>
            <H3 icon={Languages}>Languages supported natively</H3>
            <ul className="mt-4 grid grid-cols-2 gap-2 text-sm text-slate-700">
              {[
                "English (EN)",
                "Spanish (ES)",
                "Portuguese (PT)",
                "French (FR)",
                "German (DE)",
              ].map((l) => (
                <li key={l}
                    className="flex items-center gap-2 rounded-md bg-slate-50 px-3 py-2 ring-1 ring-inset ring-slate-200">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" />
                  <span className="font-medium">{l}</span>
                </li>
              ))}
            </ul>
            <p className="mt-4 text-sm text-slate-600">
              We auto-detect a contract's language and cite the right legal
              framework for that jurisdiction. One-click translation between
              the native and English report views on every scan.
            </p>
          </Card>
        </div>
      </section>

      {/* Integrations */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <Card>
          <H3 icon={Plug}>Integrations & data flow</H3>
          <div className="mt-6 overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500 border-b">
                  <th className="px-2 py-2">Integration</th>
                  <th className="px-2 py-2">Scope</th>
                  <th className="px-2 py-2">Token security</th>
                  <th className="px-2 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                <Row name="Google Drive" scope="Read-only via official Picker — we only see files you explicitly select"
                     token="Fernet-encrypted per-user refresh token; revokable from Settings" />
                <Row name="Slack" scope="Write-only to channel of your choice (critical/high-risk alerts)"
                     token="Fernet-encrypted per-user access token; one-click disconnect" />
                <Row name="Resend (email)" scope="Outbound transactional + weekly digests"
                     token="Server-side API key; granular per-channel opt-out" />
                <Row name="Claude 4.5 Sonnet (Emergent LLM)" scope="Inference on uploaded documents"
                     token="Provider-managed; no document retention, no training use" />
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      {/* Sub-processors */}
      <section className="max-w-5xl mx-auto px-6 pb-20">
        <Card>
          <H3 icon={Server}>Sub-processors</H3>
          <ul className="mt-4 space-y-3 text-sm">
            <SubP name="Anthropic (Claude)" purpose="LLM inference on scanned documents" region="US" />
            <SubP name="Emergent" purpose="Application hosting, JWT auth, MongoDB" region="US/EU" />
            <SubP name="Resend" purpose="Outbound transactional email" region="US/EU" />
            <SubP name="Google" purpose="Drive import (opt-in only)" region="US/EU" />
            <SubP name="Slack" purpose="Channel alerting (opt-in only)" region="US" />
          </ul>
          <p className="mt-4 text-xs text-slate-500">
            We notify customers in writing 30 days before adding a new
            sub-processor that materially changes data flow.
          </p>
        </Card>
      </section>

      {/* CTA */}
      <section className="max-w-3xl mx-auto px-6 pb-24 text-center">
        <h2 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">
          Got a security questionnaire?
        </h2>
        <p className="mt-3 text-slate-600">
          We've answered most of them. Drop us a note and we'll send a
          completed SIG/CAIQ within 1 business day.
        </p>
        <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <Button asChild className="bg-blue-600 hover:bg-blue-700 text-white h-11 px-6 text-sm font-semibold">
            <a href="mailto:support@agentflow.ventures?subject=Security%20questionnaire%20request">
              <Mail className="h-4 w-4 mr-2" /> Email security@
            </a>
          </Button>
          <Button asChild variant="outline" className="h-11 px-6 text-sm font-semibold border-slate-300 hover:bg-slate-50">
            <Link to="/register">
              Start free <ArrowRight className="h-4 w-4 ml-2" />
            </Link>
          </Button>
        </div>
        <p className="mt-8 text-xs text-slate-400">
          Last updated: February 2026. Full Privacy Policy at{" "}
          <Link to="/privacy" className="underline">/privacy</Link>.
        </p>
      </section>
    </div>
  );
}

function Pillar({ icon: Icon, title, body, tone = "blue" }) {
  const tones = {
    blue: "bg-blue-50/60 text-blue-700 ring-blue-200",
    emerald: "bg-emerald-50/60 text-emerald-700 ring-emerald-200",
    amber: "bg-amber-50/60 text-amber-700 ring-amber-200",
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-lg ring-1 ring-inset ${tones[tone]}`}>
        <Icon className="h-4 w-4" />
      </div>
      <h3 className="mt-3 font-display font-semibold text-slate-900 text-base">{title}</h3>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}

function Card({ children }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 sm:p-7 shadow-sm">
      {children}
    </div>
  );
}

function H3({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-2">
      <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200">
        <Icon className="h-3.5 w-3.5" />
      </span>
      <h2 className="font-display font-semibold text-slate-900 text-lg tracking-tight">
        {children}
      </h2>
    </div>
  );
}

function Row({ name, scope, token }) {
  return (
    <tr className="text-sm">
      <td className="px-2 py-3 font-medium text-slate-900">{name}</td>
      <td className="px-2 py-3 text-slate-600">{scope}</td>
      <td className="px-2 py-3 text-slate-600">{token}</td>
      <td className="px-2 py-3">
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200 px-2 py-0.5 text-[11px] font-semibold">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Live
        </span>
      </td>
    </tr>
  );
}

function SubP({ name, purpose, region }) {
  return (
    <li className="flex items-start justify-between gap-4 rounded-lg bg-slate-50 px-3 py-2.5 ring-1 ring-inset ring-slate-200">
      <div>
        <div className="font-medium text-slate-900">{name}</div>
        <div className="text-xs text-slate-500">{purpose}</div>
      </div>
      <span className="text-[11px] uppercase tracking-wider font-semibold text-slate-500 tabular-nums shrink-0 mt-0.5">
        {region}
      </span>
    </li>
  );
}
